import { useCallback } from 'react';
import PropTypes from 'prop-types';

import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import AutocompleteMui from '@mui/material/Autocomplete';
import TextFieldMui from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import CustomOption from 'src/components/ui/CustomOption';

import { GroupItems } from './styled/GroupItems';
import { GroupHead } from './styled/GroupHead';

const Autocomplete = (props) => {
	const {
		label,
		error,
		helperText,
		placeholder,
		options = [],
		belowLineLabel = 'Остальные',
		selectOnly,
		groupBy: groupByProps,
		...rest
	} = props;

	const getEquality = useCallback((option, selected) => {
		if (selected?.value !== undefined && option?.value !== undefined) {
			return option.value === selected.value;
		} else if (option?.value !== undefined) {
			return option.value === selected;
		} else {
			return option === selected;
		}
	}, []);

	const groupBy = useCallback(
		(option) => {
			if (option.belowLine) {
				return belowLineLabel;
			}
			return option.group;
		},
		[belowLineLabel],
	);

	const inputNode = (
		<AutocompleteMui
			isOptionEqualToValue={getEquality}
			noOptionsText="Ничего не найдено"
			filterOptions={selectOnly ? (all) => all : undefined}
			getOptionLabel={(option) => {
				if (typeof option === 'string') return option;
				return option?.label ?? '';
			}}
			renderInput={(params) => {
				return (
					<TextFieldMui
						placeholder={placeholder}
						error={!!error}
						{...params}
						InputProps={{
							...params.InputProps,
							sx: { backgroundColor: 'none', userSelect: 'none' },
						}}
						inputProps={{
							...params.inputProps,
							readOnly: selectOnly,
						}}
					/>
				);
			}}
			groupBy={groupByProps !== undefined ? groupByProps : groupBy}
			renderOption={(optionProps, option, _state, ownerState) => {
				const { key, ...other } = option;
				const { key: keyProps, ...otherProps } = optionProps;

				const label = ownerState.getOptionLabel(option);

				return (
					<Box key={key} component="li" {...otherProps}>
						<CustomOption {...other} label={label} />
					</Box>
				);
			}}
			renderGroup={(params) => {
				return (
					<li key={params.key}>
						{params.group && (
							<GroupHead>
								<Typography variant="subtitle2" color="secondary">
									{params.group}
								</Typography>
							</GroupHead>
						)}
						<GroupItems>{params.children}</GroupItems>
					</li>
				);
			}}
			options={options}
			autoHighlight
			selectOnFocus
			{...rest}
		/>
	);

	return (
		<Stack spacing={1}>
			{label && <InputLabel>{label}</InputLabel>}
			<FormControl>
				{inputNode}
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
				{error && <FormHelperText error>{error}</FormHelperText>}
			</FormControl>
		</Stack>
	);
};

Autocomplete.propTypes = {
	...AutocompleteMui.propTypes, // https://mui.com/material-ui/api/autocomplete/#props
	label: PropTypes.string,
	error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	helperText: PropTypes.string,
	renderInput: PropTypes.func,
	selectOnly: PropTypes.bool,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			...CustomOption.propTypes,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),
	),
};

export default Autocomplete;
